import React, { useMemo } from "react";
import { formatId } from "../../../../functions/functions";
import defaultFootball from '../../../../assets/escudo-default-foot.png';
import defaultOtherSport from '../../../../assets/escudo-default-general.png';
import './subtitle.sass';

/**
 * Componente de título secundario para ligas deportivas optimizado
 * Soporta links si se proporciona linkLiga
 * Muestra una imagen de la liga o un recuadro gris por defecto
 * 
 * @param {Object} props
 * @param {string} props.leagueName - Nombre de la liga
 * @param {string} props.linkLiga - URL del link de la liga (opcional)
 * @param {string} props.leagueImage - URL de la imagen de la liga (opcional)
 * @param {boolean} props.isFiltered - Si está filtrado por liga (aplica clase centrada)
 */
const SecondaryTitle = React.memo(({ leagueName, linkLiga, leagueImage, isFiltered, sport }) => {
  
  // Obtener imagen por defecto según el deporte
  const getDefaultImage = (sportCode) => {
    return sportCode === 'FOOT' ? defaultFootball : defaultOtherSport;
  };
  
  // Memoizar valores calculados
  const { formattedId, displayName, hasLink, imageToDisplay } = useMemo(() => {
    const id = leagueName ? formatId(leagueName) : 'default-league';
    const name = leagueName ? String(leagueName).toUpperCase() : 'LIGA';
    const link = linkLiga && String(linkLiga).trim().length > 0;
    const image = leagueImage && String(leagueImage).trim().length > 0;
    const displayImage = image ? leagueImage : getDefaultImage(sport);
    
    return { formattedId: id, displayName: name, hasLink: link, imageToDisplay: displayImage };
  }, [leagueName, linkLiga, leagueImage, sport]);
  
  // Renderizar el título como link o como texto
  const titleContent = hasLink ? (
    <a 
      href={linkLiga} 
      target="_parent"
      rel="noopener noreferrer"
      className="title-league-link"
      onClick={(e) => e.stopPropagation()}
    >
      {displayName}
    </a>
  ) : (
    <span>{displayName}</span>
  );
  
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.nextElementSibling.style.display = 'block';
  };

  const imageElement = (
    <img 
      src={imageToDisplay} 
      alt={leagueName || 'Liga'}
      className={`league-image ${!hasLink ? 'league-image-no-link' : ''}`}
      onError={handleImageError}
    />
  );

  return (
    <div
      id={formattedId}
      className={`container-title-league ${isFiltered ? 'filtered' : ''}`}
      role="button"
      tabIndex={0}
      aria-controls={`${formattedId}-content`}
      aria-label={leagueName || 'liga'}
    >
      {/* Imagen de la liga o imagen por defecto según el deporte */}
      <div className="league-image-container">
        {hasLink ? (
          <a 
            href={linkLiga}
            target="_parent"
            rel="noopener noreferrer"
            className="league-image-link"
            onClick={(e) => e.stopPropagation()}
          >
            {imageElement}
          </a>
        ) : imageElement}
      </div>

      <h3 className="title-league">
        {titleContent}
      </h3>
    </div>
  );
});

SecondaryTitle.displayName = 'SecondaryTitle';
export default SecondaryTitle;